import PropTypes from 'prop-types';
import React, { useState, useEffect } from 'react';
import { FormattedMessage } from 'react-intl';
import { useLazyQuery, useMutation } from '@apollo/client';

import Modal from 'components/ui/Modal';
import { COMFORT, PLUS, SP } from 'constants/classOfService';
import { ACTION_CODES } from 'constants/errors';
import { bookingShape, historyShape, userShape } from 'constants/propTypes';
import AgentPortalError from 'errors/AgentPortalError';
import { PROCESS_FREE_UPGRADE, REVERT_CHANGE } from 'store/mutations';

import { FREE_UPGRADE_EXCHANGE_SEARCH, GET_VOUCHER_BY_TYPE_AND_PERIOD } from '../../../../../store/queries';
import { StyledCtaButtons } from '../AfterSalesCtas.styles';
import { directionsOfSelectedSegments, selectedItemsHaveLuggageExtras } from '../AfterSalesCtas.utils';

import {
  buildGetVouchersQuery,
  buildProcessFreeUpgradeSegments,
  buildUpgradableFareInfo,
  getMaxTicketDifference,
  hasBookingBeenUpgradedForFree,
  isFreeUpgradePossible
} from './FreeUpgradeCta.utils';
import { FreeUpgradeUnavailableMessages } from './FreeUpgradeUnavailableMessages';
import {
  FETCH_POLICY,
  FREE_UPGRADE_MAX_AMOUNT_PER_JOURNEY,
  FREE_UPGRADE_MAX_AMOUNT_PER_PASSENGER,
  INITIAL_MODAL_STATE,
  MONTHLY_FREE_UPGRADE_GBP_BUDGET
} from './constants';
import BlockTicketsWithExtraLuggageWarning from '../../AfterSalesWarning/BlockTicketsWithExtraLuggageWarning/BlockTicketsWithExtraLuggageWarning';

const FreeUpgradeCta = ({ history: { push }, label, disabled, booking, selectedItemRefs, isMenuOpen = false, setDisabled }) => {
  const [loading, setLoading] = useState(false);
  const [unavailableFreeUpgrade, setUnavailableFreeUpgrade] = useState(INITIAL_MODAL_STATE);
  const { payments, salesChannelCode, vouchers, reference, currency } = booking;
  const [showBlockTicketsWithExtraLuggageWarning, setShowBlockTicketsWithExtraLuggageWarning] = useState(false);

  const hasLuggageExtras = selectedItemsHaveLuggageExtras(booking, selectedItemRefs);

  const [vouchersQuery, { error: vouchersError }] = useLazyQuery(GET_VOUCHER_BY_TYPE_AND_PERIOD, {
    fetchPolicy: FETCH_POLICY,
    onCompleted: ({ getVouchersByTypeAndPeriod }) => onVouchersQueryCompleted(getVouchersByTypeAndPeriod)
  });
  const [searchQuery, { error: searchError }] = useLazyQuery(FREE_UPGRADE_EXCHANGE_SEARCH, {
    fetchPolicy: FETCH_POLICY,
    onCompleted: ({ freeUpgradeJourneySearch }) => onSearchQueryCompleted(freeUpgradeJourneySearch)
  });
  const [freeUpgradeMutation, { error: freeUpgradeError }] = useMutation(PROCESS_FREE_UPGRADE, {
    fetchPolicy: FETCH_POLICY,
    onCompleted: (data) => onFreeUpgradeMutationCompleted(data)
  });

  if (vouchersError || searchError || freeUpgradeError) {
    setLoading(false);
    throw new AgentPortalError(vouchersError || searchError || freeUpgradeError, ACTION_CODES.REDIRECT_TO_CUSTOM_PAGE, {
      errorCTARedirect: `/manage/bookings/${reference}/journeys`
    });
  }

  const [revertChange] = useMutation(REVERT_CHANGE, {
    fetchPolicy: FETCH_POLICY
  });

  const handleClick = async () => {
    if (hasLuggageExtras) {
      setShowBlockTicketsWithExtraLuggageWarning(true);
      return;
    }

    setLoading(true);
    const variables = buildGetVouchersQuery({ currency });
    return await vouchersQuery({ variables: { ...variables } });
  };

  const onVouchersQueryCompleted = async (vouchersResponse) => {
    const maxFreeUpgradeReached = vouchersResponse?.totalValueInCurrency >= MONTHLY_FREE_UPGRADE_GBP_BUDGET;
    const bookingAlreadyUpgradedForFree = hasBookingBeenUpgradedForFree({
      payments,
      bookingVouchers: vouchers,
      getVouchersResponse: vouchersResponse?.vouchers
    });
    const shouldBlockFreeUpgrade = maxFreeUpgradeReached || bookingAlreadyUpgradedForFree;

    if (shouldBlockFreeUpgrade) {
      setLoading(false);
      if (maxFreeUpgradeReached) {
        return setUnavailableFreeUpgrade({
          ...INITIAL_MODAL_STATE,
          isOpen: true,
          maxValueReached: true
        });
      }

      return setUnavailableFreeUpgrade({
        ...INITIAL_MODAL_STATE,
        isOpen: true,
        bookingAlreadyUpgradedForFree: true
      });
    }

    return await searchQuery({
      variables: {
        reference,
        exchangeItems: selectedItemRefs.map(Number),
        currency
      }
    });
  };

  const onSearchQueryCompleted = async (searchResponse) => {
    const directionOfUpgradeItems = directionsOfSelectedSegments({ selectedItemRefs, booking })?.[0];
    const upgradableFare = searchResponse?.[directionOfUpgradeItems]?.journeys?.[0]?.fares?.find(({ classOfService }) =>
      [SP, PLUS, COMFORT].includes(classOfService?.code)
    );

    if (upgradableFare?.bookingItems?.length === 0) {
      setLoading(false);
      const upgradableFareInfo = buildUpgradableFareInfo({ booking, selectedItemRefs });
      return setUnavailableFreeUpgrade({
        ...INITIAL_MODAL_STATE,
        isOpen: true,
        freeUpgradeUnavailable: upgradableFareInfo
      });
    }

    return await freeUpgradeMutation({
      variables: {
        bookingReference: reference,
        currency,
        segments: buildProcessFreeUpgradeSegments({ fare: upgradableFare, direction: directionOfUpgradeItems })
      }
    });
  };

  const onFreeUpgradeMutationCompleted = ({ processFreeUpgrade = {} }) => {
    setLoading(false);

    const { outbound, inbound, totalPriceToBePaid } = processFreeUpgrade;

    const exceedMaxAmountPerJourney = totalPriceToBePaid >= FREE_UPGRADE_MAX_AMOUNT_PER_JOURNEY;
    const exceedMaxAmountPerPassengerOubound =
      outbound.length > 0 && getMaxTicketDifference(outbound) >= FREE_UPGRADE_MAX_AMOUNT_PER_PASSENGER;
    const exceedMaxAmountPerPassengerInbound =
      inbound.length > 0 && getMaxTicketDifference(inbound) >= FREE_UPGRADE_MAX_AMOUNT_PER_PASSENGER;

    if (exceedMaxAmountPerJourney) {
      setUnavailableFreeUpgrade({
        ...INITIAL_MODAL_STATE,
        isOpen: true,
        maxAmountPerJourney: true
      });
    } else if (exceedMaxAmountPerPassengerOubound || exceedMaxAmountPerPassengerInbound) {
      setUnavailableFreeUpgrade({
        ...INITIAL_MODAL_STATE,
        isOpen: true,
        maxAmountPerPassenger: true
      });
    }

    if (exceedMaxAmountPerJourney || exceedMaxAmountPerPassengerOubound || exceedMaxAmountPerPassengerInbound) {
      revertChange({
        variables: {
          bookingReference: reference
        }
      });

      return;
    } else {
      return push(`/exchange/${reference}/pax?currency=${currency}&isFreeUpgrade=true`);
    }
  };

  const onCloseModal = () => setUnavailableFreeUpgrade(INITIAL_MODAL_STATE);

  const freeUpgradePossible = isFreeUpgradePossible({ booking, selectedItemRefs, salesChannelCode });

  useEffect(() => {
    setDisabled(!freeUpgradePossible);
  });

  const { isOpen, maxValueReached, bookingAlreadyUpgradedForFree, freeUpgradeUnavailable, maxAmountPerJourney, maxAmountPerPassenger } =
    unavailableFreeUpgrade;

  const toggleBlockTicketsWithExtraLuggageWarning = () => {
    setShowBlockTicketsWithExtraLuggageWarning(!showBlockTicketsWithExtraLuggageWarning);
  };

  return (
    <>
      <Modal title={<FormattedMessage id="aftersales.freeUpgrade.unavailable" />} isOpen={isOpen} onClose={onCloseModal}>
        <FreeUpgradeUnavailableMessages
          message={{ maxValueReached, bookingAlreadyUpgradedForFree, freeUpgradeUnavailable, maxAmountPerJourney, maxAmountPerPassenger }}
        />
      </Modal>

      <BlockTicketsWithExtraLuggageWarning
        isOpen={showBlockTicketsWithExtraLuggageWarning}
        closeModal={toggleBlockTicketsWithExtraLuggageWarning}
      />
      <StyledCtaButtons
        type="button"
        data-cy="free-upgrade-cta"
        disabled={disabled || loading}
        label={label}
        loading={loading}
        onClick={handleClick}
        date-testId="free-upgrade-cta"
        isMenuOpen={isMenuOpen}
      >
        {label}
      </StyledCtaButtons>
    </>
  );
};

FreeUpgradeCta.propTypes = {
  user: userShape,
  booking: bookingShape,
  history: historyShape,
  label: PropTypes.element,
  disabled: PropTypes.bool,
  selectedItemRefs: PropTypes.arrayOf(PropTypes.string),
  isMenuOpen: PropTypes.bool,
  setDisabled: PropTypes.func
};

export default FreeUpgradeCta;
